import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Box, Alert, Select, MenuItem, FormControl,
  InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, TextField, Button, Tabs, Tab
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import DownloadIcon from '@mui/icons-material/Download';
import billingApi from '../../services/billingApi';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

const PERIOD_OPTIONS = [
  { value: 'day', label: 'Last 24 Hours' },
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
  { value: 'quarter', label: 'Last 90 Days' },
  { value: 'year', label: 'Last Year' }
];

const FEATURE_LABELS = {
  'limits.ai_operations_per_month': 'AI Operations',
  'limits.ai_commands_per_month': 'AI Commands',
  'limits.active_tasks': 'Active Tasks',
  'optimizer.strategies': 'Optimizer Strategies',
  'optimizer.custom_strategies': 'Custom Strategies',
  'templates.presets': 'Template Presets',
  'templates.full_customization': 'Template Customization',
  'export.json_export': 'JSON Export',
  'scheduling.dependencies': 'Dependencies',
  'scheduling.travel_time': 'Travel Time',
  'calendar.auto_sync': 'Calendar Auto-Sync',
  'data.export': 'Data Export',
  'data.mcp_access': 'MCP Access'
};

const EVENT_TYPES = ['used', 'blocked', 'limit_reached'];

function StatCard({ title, value, subtitle, color }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        <Typography variant="h4" sx={{ color: color || 'inherit', my: 0.5 }}>{value}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
}

// ─── CSV Export ──────────────────────────────────────────────────────────
function downloadCsv(rows, columns, filename) {
  const header = columns.map(c => c.headerName).join(',');
  const lines = rows.map(row =>
    columns.map(c => {
      let val = c.valueGetter ? c.valueGetter({ row }) : row[c.field];
      if (val === null || val === undefined) val = '';
      val = String(val);
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        val = '"' + val.replace(/"/g, '""') + '"';
      }
      return val;
    }).join(',')
  );
  const csv = [header, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// For CSV export, use raw values (no rendering)
const CSV_COLUMNS = [
  { field: 'created_at', headerName: 'Timestamp' },
  { field: 'productId', headerName: 'Product' },
  { field: 'user_id', headerName: 'User ID' },
  { field: 'user_name', headerName: 'User Name' },
  { field: 'user_email', headerName: 'Email' },
  { field: 'planId', headerName: 'Plan' },
  { field: 'feature_key', headerName: 'Feature' },
  { field: 'event_type', headerName: 'Event' },
  { field: 'quantity', headerName: 'Quantity' },
  { field: 'input_tokens', headerName: 'Input Tokens' },
  { field: 'output_tokens', headerName: 'Output Tokens' },
  { field: 'cost_micros', headerName: 'Cost (microdollars)' },
  { field: 'endpoint', headerName: 'Endpoint' }
];

export default function UsageAnalytics() {
  const [tab, setTab] = useState(0);

  // Summary state
  const [period, setPeriod] = useState('month');
  const [product, setProduct] = useState('');
  const [groupBy, setGroupBy] = useState('feature');
  const [usage, setUsage] = useState(null);
  const [topUsers, setTopUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Raw data state
  const [rawEvents, setRawEvents] = useState([]);
  const [rawLoading, setRawLoading] = useState(false);
  const [rawError, setRawError] = useState('');
  const [rawFilters, setRawFilters] = useState({ from: '', to: '' });

  // Raw data columns
  const rawColumns = useMemo(() => [
    {
      field: 'created_at', headerName: 'Timestamp', width: 170,
      type: 'date',
      valueGetter: (params) => params.row.created_at ? new Date(params.row.created_at).toLocaleString() : ''
    },
    {
      field: 'productId', headerName: 'Product', width: 130,
      type: 'singleSelect',
      filterOptions: ['resume-optimizer', 'juggler'],
    },
    {
      field: 'user_name', headerName: 'User', width: 140,
      valueGetter: (params) => params.row.user_name || params.row.user_id?.substring(0, 12) + '...'
    },
    { field: 'user_email', headerName: 'Email', width: 180 },
    { field: 'planId', headerName: 'Plan', width: 120 },
    {
      field: 'feature_key', headerName: 'Feature', width: 200,
      type: 'singleSelect',
      filterOptions: Object.keys(FEATURE_LABELS),
      renderCell: (row) => FEATURE_LABELS[row.feature_key] || row.feature_key,
    },
    {
      field: 'event_type', headerName: 'Event', width: 110,
      type: 'singleSelect',
      filterOptions: EVENT_TYPES,
      renderCell: (row) => {
        const colors = { used: 'success', blocked: 'warning', limit_reached: 'error' };
        return <Chip label={row.event_type} size="small" color={colors[row.event_type] || 'default'} />;
      }
    },
    { field: 'quantity', headerName: 'Qty', width: 70, type: 'numeric' },
    { field: 'input_tokens', headerName: 'In Tokens', width: 100, type: 'numeric' },
    { field: 'output_tokens', headerName: 'Out Tokens', width: 100, type: 'numeric' },
    {
      field: 'cost_micros', headerName: 'Cost', width: 90,
      type: 'numeric',
      valueGetter: (params) => params.row.cost_micros ? '$' + (params.row.cost_micros / 1000000).toFixed(4) : ''
    },
    { field: 'endpoint', headerName: 'Endpoint', width: 250 }
  ], []);

  async function loadSummary() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ period, group_by: groupBy });
      if (product) params.append('product', product);

      const [usageRes, topRes] = await Promise.all([
        billingApi.get(`/api/admin/usage?${params}`),
        billingApi.get(`/api/admin/usage/top-users?period=${period}${product ? '&product=' + product : ''}`)
      ]);

      setUsage(usageRes);
      setTopUsers(topRes);
    } catch (err) {
      setError(err.message || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }

  const loadRawData = useCallback(async () => {
    setRawLoading(true);
    setRawError('');
    try {
      const params = new URLSearchParams();
      if (rawFilters.from) params.set('from', rawFilters.from);
      if (rawFilters.to) params.set('to', rawFilters.to);

      const res = await billingApi.get(`/api/admin/usage/events?${params}`);
      setRawEvents(res.events || []);
    } catch (err) {
      setRawError(err.message || 'Failed to load events');
    } finally {
      setRawLoading(false);
    }
  }, [rawFilters]);

  // Load summary data
  useEffect(() => {
    if (tab !== 0) return;
    loadSummary();
  }, [period, product, groupBy, tab]);

  // Load raw data
  useEffect(() => {
    if (tab !== 1) return;
    loadRawData();
  }, [loadRawData, tab]);

  function handleExportCsv() {
    const date = new Date().toISOString().split('T')[0];
    downloadCsv(rawEvents, CSV_COLUMNS, `usage-events-${date}.csv`);
  }

  const formatTokens = (n) => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  };

  const formatCost = (micros) => {
    if (!micros) return '$0.00';
    return '$' + (micros / 1000000).toFixed(4);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Usage Analytics</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Summary" />
        <Tab label="Raw Data" />
      </Tabs>

      {/* ─── Summary Tab ─── */}
      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Period</InputLabel>
              <Select value={period} label="Period" onChange={e => setPeriod(e.target.value)}>
                {PERIOD_OPTIONS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Product</InputLabel>
              <Select value={product} label="Product" onChange={e => setProduct(e.target.value)}>
                <MenuItem value="">All Products</MenuItem>
                <MenuItem value="resume-optimizer">ClimbRS</MenuItem>
                <MenuItem value="juggler">StriveRS</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Group By</InputLabel>
              <Select value={groupBy} label="Group By" onChange={e => setGroupBy(e.target.value)}>
                <MenuItem value="feature">Feature</MenuItem>
                <MenuItem value="plan">Plan</MenuItem>
                <MenuItem value="product">Product</MenuItem>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="day">Day</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <AdminPageSkeleton bare rows={5} />
          ) : usage && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <StatCard title="Total Events" value={usage.summary?.total_events?.toLocaleString() || '0'} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard title="Unique Users" value={usage.summary?.unique_users || '0'} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    title="Total Tokens"
                    value={formatTokens((usage.summary?.total_tokens?.input || 0) + (usage.summary?.total_tokens?.output || 0))}
                    subtitle={`In: ${formatTokens(usage.summary?.total_tokens?.input)} / Out: ${formatTokens(usage.summary?.total_tokens?.output)}`}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    title="Estimated Cost"
                    value={usage.summary?.estimated_cost_dollars ? '$' + usage.summary.estimated_cost_dollars : '$0.00'}
                    color="#2D6A4F"
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 1 }}>Breakdown by {groupBy}</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Used</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Blocked</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Limit Hit</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Tokens (In/Out)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(usage.breakdown || []).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{FEATURE_LABELS[row.group] || row.group}</TableCell>
                        <TableCell align="right">{row.total?.toLocaleString()}</TableCell>
                        <TableCell align="right">{row.used?.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          {row.blocked > 0 ? <Chip label={row.blocked} size="small" color="warning" /> : '0'}
                        </TableCell>
                        <TableCell align="right">
                          {row.limit_reached > 0 ? <Chip label={row.limit_reached} size="small" color="error" /> : '0'}
                        </TableCell>
                        <TableCell align="right">
                          {formatTokens(row.total_input_tokens)} / {formatTokens(row.total_output_tokens)}
                        </TableCell>
                        <TableCell align="right">{formatCost(row.total_cost_micros)}</TableCell>
                      </TableRow>
                    ))}
                    {(!usage.breakdown || usage.breakdown.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No usage data for this period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {topUsers?.users?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>Top Users</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Usage</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Tokens</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Cost</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topUsers.users.map((user, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              {user.user_name || <Typography component="span" variant="caption" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.75rem' }}>{user.user_id?.substring(0, 8)}...</Typography>}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{user.user_email || '—'}</TableCell>
                            <TableCell>{user.productId}</TableCell>
                            <TableCell><Chip label={user.planId} size="small" variant="outlined" /></TableCell>
                            <TableCell align="right">{user.total_usage?.toLocaleString()}</TableCell>
                            <TableCell align="right">
                              {formatTokens((Number(user.total_input_tokens) || 0) + (Number(user.total_output_tokens) || 0))}
                            </TableCell>
                            <TableCell align="right">{formatCost(user.total_cost_micros)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ─── Raw Data Tab ─── */}
      {tab === 1 && (
        <>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" type="date" label="From" value={rawFilters.from}
              onChange={e => setRawFilters(f => ({ ...f, from: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <TextField size="small" type="date" label="To" value={rawFilters.to}
              onChange={e => setRawFilters(f => ({ ...f, to: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <Button
              variant="outlined" size="small" startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
              disabled={rawEvents.length === 0}
            >
              Export CSV ({rawEvents.length} rows)
            </Button>
          </Box>

          {rawError && <Alert severity="error" sx={{ mb: 2 }}>{rawError}</Alert>}

          <Paper variant="outlined" sx={{ height: 600 }}>
            <AdminTable
              rows={rawEvents}
              columns={rawColumns}
              defaultSort={[]}
              loading={rawLoading}
              defaultPageSize={100}
              getRowId={(row) => row.id}
            />
          </Paper>
        </>
      )}
    </Container>
  );
}
